import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import api from "../api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, Trash2 } from "lucide-react";
import { DocTemplatesPanel } from "../components/DocTemplatesPanel";

const RecordsWithDocs = ({ id, category, children }) => {
  const { t } = useApp();
  return (
    <Tabs defaultValue="records">
      <TabsList className="mb-4">
        <TabsTrigger value="records" data-testid={`${id}-subtab-records`}>{t("forms.records")}</TabsTrigger>
        <TabsTrigger value="docs" data-testid={`${id}-subtab-docs`}>{t("forms.docsTab")}</TabsTrigger>
      </TabsList>
      <TabsContent value="records">{children}</TabsContent>
      <TabsContent value="docs"><DocTemplatesPanel category={category} /></TabsContent>
    </Tabs>
  );
};

const riskBadge = (level, t) => {
  const map = {
    low: ["bg-emerald-500/15 text-emerald-500 border-emerald-500/30", t("aml.low")],
    medium: ["bg-amber-500/15 text-amber-500 border-amber-500/30", t("aml.medium")],
    high: ["bg-red-500/15 text-red-500 border-red-500/30", t("aml.high")],
  };
  const [cls, label] = map[level] || map.low;
  return <Badge className={cls} data-testid="aml-risk-assessment-badge">{label}</Badge>;
};

function AmlTab() {
  const { t, entity } = useApp();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", risk_level: "low", verification_status: "pending", beneficial_owner: "", check_date: "", notes: "" });

  const load = useCallback(() => {
    api.get(`/aml?entity_id=${entity}`).then((r) => setItems(r.data)).catch(() => {});
  }, [entity]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    try {
      await api.post("/aml", { ...form, entity_id: entity, check_date: form.check_date || null });
      toast.success(t("msg.saved"));
      setOpen(false);
      setForm({ subject: "", risk_level: "low", verification_status: "pending", beneficial_owner: "", check_date: "", notes: "" });
      load();
    } catch { toast.error(t("msg.error")); }
  };
  const remove = async (id) => {
    try { await api.delete(`/aml/${id}`); toast.success(t("msg.deleted")); load(); }
    catch { toast.error(t("msg.error")); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-aml-button"><Plus className="h-4 w-4 mr-2" />{t("aml.new")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("aml.new")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>{t("aml.subject")}</Label>
                <Input value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="aml-subject-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>{t("aml.risk")}</Label>
                  <Select value={form.risk_level} onValueChange={(v) => set("risk_level", v)}>
                    <SelectTrigger data-testid="aml-risk-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">{t("aml.low")}</SelectItem>
                      <SelectItem value="medium">{t("aml.medium")}</SelectItem>
                      <SelectItem value="high">{t("aml.high")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>{t("common.status")}</Label>
                  <Select value={form.verification_status} onValueChange={(v) => set("verification_status", v)}>
                    <SelectTrigger data-testid="aml-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">{t("aml.pending")}</SelectItem>
                      <SelectItem value="verified">{t("aml.verified")}</SelectItem>
                      <SelectItem value="expired">{t("aml.expiredV")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>{t("aml.owner")}</Label>
                <Input value={form.beneficial_owner} onChange={(e) => set("beneficial_owner", e.target.value)} data-testid="aml-owner-input" /></div>
              <div><Label>{t("aml.checkDate")}</Label>
                <Input type="date" value={form.check_date} onChange={(e) => set("check_date", e.target.value)} data-testid="aml-checkdate-input" /></div>
              <div><Label>{t("common.notes")}</Label>
                <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="aml-notes-input" /></div>
              <Button className="w-full" onClick={submit} data-testid="aml-submit-button">{t("common.save")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {items.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">{t("common.noData")}</div> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("aml.subject")}</TableHead>
              <TableHead>{t("aml.risk")}</TableHead>
              <TableHead>{t("aml.owner")}</TableHead>
              <TableHead>{t("common.status")}</TableHead>
              <TableHead>{t("aml.checkDate")}</TableHead>
              <TableHead>{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((r) => (
              <TableRow key={r.id} data-testid={`aml-row-${r.id}`}>
                <TableCell className="font-medium">{r.subject}</TableCell>
                <TableCell>{riskBadge(r.risk_level, t)}</TableCell>
                <TableCell>{r.beneficial_owner || "—"}</TableCell>
                <TableCell>
                  <Badge variant={r.verification_status === "verified" ? "default" : r.verification_status === "expired" ? "destructive" : "secondary"}>
                    {r.verification_status === "verified" ? t("aml.verified") : r.verification_status === "expired" ? t("aml.expiredV") : t("aml.pending")}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-xs">{r.check_date || "—"}</TableCell>
                <TableCell>
                  <Button size="icon" variant="ghost" data-testid={`delete-aml-${r.id}`} onClick={() => remove(r.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function PrivacyTab() {
  const { t, entity } = useApp();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ treatment: "", purpose: "", legal_basis: "", data_categories: "", retention: "", notes: "" });

  const load = useCallback(() => {
    api.get(`/privacy?entity_id=${entity}`).then((r) => setItems(r.data)).catch(() => {});
  }, [entity]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    try {
      await api.post("/privacy", { ...form, entity_id: entity });
      toast.success(t("msg.saved"));
      setOpen(false);
      setForm({ treatment: "", purpose: "", legal_basis: "", data_categories: "", retention: "", notes: "" });
      load();
    } catch { toast.error(t("msg.error")); }
  };
  const remove = async (id) => {
    try { await api.delete(`/privacy/${id}`); toast.success(t("msg.deleted")); load(); }
    catch { toast.error(t("msg.error")); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-privacy-button"><Plus className="h-4 w-4 mr-2" />{t("privacy.new")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("privacy.new")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>{t("privacy.treatment")}</Label>
                <Input value={form.treatment} onChange={(e) => set("treatment", e.target.value)} data-testid="privacy-treatment-input" /></div>
              <div><Label>{t("privacy.purpose")}</Label>
                <Input value={form.purpose} onChange={(e) => set("purpose", e.target.value)} data-testid="privacy-purpose-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>{t("privacy.legalBasis")}</Label>
                  <Input value={form.legal_basis} onChange={(e) => set("legal_basis", e.target.value)} data-testid="privacy-legalbasis-input" /></div>
                <div><Label>{t("privacy.retention")}</Label>
                  <Input value={form.retention} onChange={(e) => set("retention", e.target.value)} data-testid="privacy-retention-input" /></div>
              </div>
              <div><Label>{t("privacy.categories")}</Label>
                <Input value={form.data_categories} onChange={(e) => set("data_categories", e.target.value)} data-testid="privacy-categories-input" /></div>
              <div><Label>{t("common.notes")}</Label>
                <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="privacy-notes-input" /></div>
              <Button className="w-full" onClick={submit} data-testid="privacy-submit-button">{t("common.save")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {items.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">{t("common.noData")}</div> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("privacy.treatment")}</TableHead>
              <TableHead>{t("privacy.purpose")}</TableHead>
              <TableHead>{t("privacy.legalBasis")}</TableHead>
              <TableHead>{t("privacy.categories")}</TableHead>
              <TableHead>{t("privacy.retention")}</TableHead>
              <TableHead>{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((r) => (
              <TableRow key={r.id} data-testid={`privacy-row-${r.id}`}>
                <TableCell className="font-medium">{r.treatment}</TableCell>
                <TableCell>{r.purpose || "—"}</TableCell>
                <TableCell>{r.legal_basis || "—"}</TableCell>
                <TableCell>{r.data_categories || "—"}</TableCell>
                <TableCell>{r.retention || "—"}</TableCell>
                <TableCell>
                  <Button size="icon" variant="ghost" data-testid={`delete-privacy-${r.id}`} onClick={() => remove(r.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

export default function Compliance() {
  const { t } = useApp();
  return (
    <div className="space-y-6" data-testid="compliance-page">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("nav.compliance")}</h1>
      <Card>
        <CardContent className="pt-6">
          <Tabs defaultValue="aml">
            <TabsList className="mb-4">
              <TabsTrigger value="aml" data-testid="tab-aml">{t("aml.title")}</TabsTrigger>
              <TabsTrigger value="privacy" data-testid="tab-privacy">{t("privacy.title")}</TabsTrigger>
            </TabsList>
            <TabsContent value="aml"><RecordsWithDocs id="aml" category="antiriciclaggio"><AmlTab /></RecordsWithDocs></TabsContent>
            <TabsContent value="privacy"><RecordsWithDocs id="privacy" category="privacy"><PrivacyTab /></RecordsWithDocs></TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
